import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import FixedActionBar from '../menus/FixedActionBar.js';

import ProductCard from '../components/ProductCard.js';
import Reassurance from '../components/Reassurance.js';
// import ComparisonFunctionsTable from '../components/ComparisonFunctionsTable.js';
import Loader from '../components/Loader.js';
import { useParams, useNavigate  } from 'react-router-dom';

import { stripeProductsAPI, stripeProductPricesAPI } from '../apis/apiStripe.js';

import '../styles/ProductPage.css';
import CountdownTimer from '../components/CountdownTimer.js';

function H01ProductPage() {
  const [H01, setH01] = useState({});
  const [loader, setLoader] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const { color } = useParams();
  const navigate = useNavigate();

  // Données des sections pour l’accordéon (fonctionnalités principales)
  const accordionData2 = [
    {
      title: 'Éclairage avant et arrière',
      content: (
        <>
          <p>
            Notre casque intelligent est muni de feux avant et arrière
            pour assurer une visibilité optimale, de jour comme de nuit.
            Les LED haute performance vous permettent d’être facilement repérable
            par les autres usagers de la route et renforcent considérablement votre sécurité.
          </p>
        </>
      )
    },
    {
      title: 'Clignotants intégrés',
      content: (
        <>
          <p>
            Grâce aux clignotants intégrés, signalez clairement et efficacement
            vos changements de direction. Des LED défilantes indiquant votre intention
            de tourner, offrant une sécurité accrue et réduisant le risque de collision.
          </p>
          <p>
            Les commandes de clignotants sont facilement accessibles via une télécommande
            fixée sur le guidon, pour un usage intuitif. Un <strong>bip sonore</strong> se déclenche
            lorsque les clignotants sont activés, vous confirmant instantanément que votre
            signalisation est bien en cours.
          </p>
        </>
      )
    },
    {
      title: 'Feu stop',
      content: (
        <>
          <p>
            Le casque détecte automatiquement votre décélération et active
            un feu stop rouge à l’arrière. Cette fonction prévient les cyclistes
            ou automobilistes derrière vous qu’un ralentissement est en cours,
            diminuant ainsi le risque de collision par l’arrière.
          </p>
        </>
      )
    },
    {
      title: '2 tailles + Molette ergonomique',
      content: (
        <>
          <p>
            Le casque est disponible en deux tailles : S/M (55 cm – 58 cm)
            et L/XL (59 cm – 63 cm). Grâce à sa molette ergonomique,
            il s’ajuste parfaitement à la forme de votre tête pour un maintien optimal.
          </p>
          <p>
            Cette technologie de serrage enveloppant améliore non seulement
            votre confort, mais renforce également votre sécurité en assurant
            un excellent maintien du casque, même en cas de choc.
          </p>
        </>
      )
    }
  ];

  // Données des sections pour l’accordéon (avantages supplémentaires)
  const accordionData = [
    {
      title: 'Léger et confortable',
      content: (
        <>
          <p>
            Ce casque a été pensé pour offrir un maximum de confort.
            Son poids plume de 350 grammes et ses mousses intérieures respirantes
            permettent de longues sorties à vélo sans gêne ni surchauffe.
          </p>
          <p>
            Conçu en matériaux innovants, il allie robustesse et légèreté,
            tout en garantissant une protection optimale de la tête.
          </p>
        </>
      )
    },
    {
      title: 'Design élégant',
      content: (
        <>
          <p>
            Ses lignes épurées et sa silhouette moderne en font un accessoire
            à la fois fonctionnel et stylé. Le casque s’intègre harmonieusement
            à votre tenue, que vous soyez adepte du vélo urbain ou que vous
            recherchiez simplement un look soigné.
          </p>
        </>
      )
    },
    {
      title: 'Autonomie renforcée',
      content: (
        <>
          <p>
            Doté d’une batterie de dernière génération, le casque peut fonctionner
            plusieurs heures avec l’éclairage et les clignotants activés.
          </p>
          <p>
            La recharge s’effectue en USB-C pour une simplicité d’utilisation
            et une compatibilité étendue.
          </p>
        </>
      )
    },
    {
      title: 'Aération optimisée',
      content: (
        <>
          <p>
            Des aérations stratégiquement placées assurent une bonne circulation
            de l’air sous le casque, réduisant la transpiration et améliorant
            la sensation de fraîcheur pendant l’effort.
          </p>
          <p>
            Grâce à sa conception respirante, vous restez au frais
            même par temps chaud.
          </p>
        </>
      )
    },
    {
      title: 'Certification & Sécurité',
      content: (
        <>
          <p>
            Le casque intelligent respecte les principales normes de sécurité
            (EN 1078) et a été soumis à des tests rigoureux pour garantir
            une protection fiable en cas d’impact.
          </p>
          <p>
            L’ajustement précis, grâce à la molette et aux sangles latérales,
            optimise la stabilité du casque et limite les risques de mouvement
            intempestif pendant un choc.
          </p>
        </>
      )
    },
  ];

  // État pour savoir quel item est ouvert, -1 = aucun
  const [openIndex, setOpenIndex] = useState(-1);

  const toggleAccordion = (index) => {
    // Si on reclique sur l’item déjà ouvert, on le referme
    // Sinon on ouvre l’item cliqué
    setOpenIndex(openIndex === index ? -1 : index);
  };

  // Récupération des produits (code inchangé, sauf références textuelles si besoin)
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const productsResponse = await stripeProductsAPI();

        if (!productsResponse || !productsResponse.data) {
          return console.warn("Aucun produit trouvé.");
        }

        const activeProducts = productsResponse.data.filter(product => product.active);

        const formattedProducts = await Promise.all(
          activeProducts.map(async (product) => {
            const priceData = product.default_price
              ? await stripeProductPricesAPI(product.id)
              : null;

            return {
              productId: product.id,
              name: product.name,
              description: product.description,
              image: product.images?.[0] || null,
              metadata: product.metadata,
              created: product.created,
              updated: product.updated,
              price: priceData,
            };
          })
        );

        const filterActivePrices = (product) => ({
          ...product,
          price: {
            ...product.price,
            data: product.price?.data?.filter(price => price.active) || []
          }
        });

        const productsWithActivePrices = formattedProducts.map(filterActivePrices);

        const filteredProductsH01 = productsWithActivePrices.filter(
          product => product.metadata.ref === "H01"
        );

        const ProductsH01 = { bgVideo: '/assets/backgrounds/videos/bg-5.mp4', data: filteredProductsH01 };
        setH01(ProductsH01);

        // Lecture de la couleur via hash (ex: #ddd1bc)
        const hashColor = window.location.hash ? window.location.hash.toUpperCase() : null;

        let defaultProduct;

        // D'abord on essaie via le hash (hex)
        if (hashColor) {
          defaultProduct = filteredProductsH01.find(
            (product) => product.metadata.color?.toUpperCase() === hashColor
          );
        }

        // Si pas trouvé, on tente via URL paramètre `/product/.../:color`
        if (!defaultProduct && color) {
          defaultProduct = filteredProductsH01.find(
            (product) => product.metadata.color === color
          );
        }

        // Sinon, on prend le premier
        setSelectedProduct(defaultProduct || filteredProductsH01[0]);
        setLoader(false);
      } catch (error) {
        console.error("Erreur lors de la récupération des produits Stripe :", error);
      }
    };

    fetchProducts();
  }, [color]);

  const handleProductSelected = (product) => {
    setSelectedProduct(product);
    navigate(`/product/casque-intelligent-lumineux-h01/${product.metadata.color}`);
  };

  return (
    <div className='product-page' style={{margin: "0 auto" }}>
      
      {/* {selectedProduct?.metadata.promo_date ? <div style={{backgroundColor: "#bbe2d2", marginTop: "-4px", position: 'fixed', width: '100%', zIndex: 99}}><CountdownTimer color="black" date={selectedProduct?.metadata.promo_date}/></div> : null} */}
      
      <div style={{maxWidth: "1200px", margin: "0 auto" }}>
        {
          !loader ? (
            <div style={{ paddingTop: 10 }}>

              <div className='mobile'>
                { H01.data && H01.bgVideo ? (
                  <ProductCard
                    products={H01}
                    modeColor="blue"
                    discover={false}
                    actions={true}
                    newProduct={true}
                    onProductSelected={handleProductSelected}
                    forcedColor={selectedProduct?.metadata?.color}
                    backgroundVideoEnabled={false}
                  />
                ) : <Loader /> }
              </div>

              <div className='desktop-block-2'>
                <div className='desktop'>
                  { H01.data && H01.bgVideo ? (
                    <ProductCard
                      products={H01}
                      modeColor="blue"
                      discover={false}
                      actions={true}
                      newProduct={true}
                      onProductSelected={handleProductSelected}
                      forcedColor={selectedProduct?.metadata?.color}
                      backgroundVideoEnabled={false}
                    />
                  ) : <Loader /> }
                </div>

                <div>
                  <h1 style={{fontSize: 55, textTransform: 'uppercase', marginLeft: '2%', marginRight: '2%'}}>Le casque lumineux et intelligent</h1>
                  
                  <p style={{fontSize: 40, margin: '40px 20px 0px 20px', color: "#333", fontWeight: 700}}>
                    Fonctionnalités
                  </p>

                  <div style={{ borderRadius: 28, padding: '20px', margin: '20px', backgroundColor: '#fff' }}>
                    {accordionData2.map((item, index) => (
                      <div key={index} style={{ marginBottom: '10px' }}>
                        <button
                          onClick={() => toggleAccordion(index)}
                          style={{
                            width: '100%',
                            textAlign: 'left',
                            padding: '10px',
                            fontSize: '16px',
                            backgroundColor: '#fff',
                            border: '1px solid #ddd',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            borderRadius: 28,
                            color: '#333'
                          }}
                        >
                          <span>{item.title}</span>
                          <ChevronDown
                            size={20}
                            style={{
                              transform: openIndex === index ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s ease'
                            }}
                          />
                        </button>

                        <div
                          style={{
                            maxHeight: openIndex === index ? '500px' : '0px',
                            overflow: 'hidden',
                            transition: 'max-height 0.3s ease',
                            borderTop: 'none',
                            padding: openIndex === index ? '10px' : '0 10px',
                            backgroundColor: '#fff'
                          }}
                        >
                          {item.content}
                        </div>
                      </div>
                    ))}
                  </div>

                  <p style={{fontSize: 40, margin: '40px 20px 0px 20px', color: "#333", fontWeight: 700}}>
                    Avantages
                  </p>
                  {/* Accordéon (deuxième groupe) */}
                  <div style={{ borderRadius: 28, padding: '20px', margin: '20px', backgroundColor: '#fff' }}>
                    {accordionData.map((item, index) => (
                      <div key={index} style={{ marginBottom: '10px' }}>
                        <button
                          onClick={() => toggleAccordion(index)}
                          style={{
                            width: '100%',
                            textAlign: 'left',
                            padding: '10px',
                            fontSize: '16px',
                            backgroundColor: '#fff',
                            border: '1px solid #ddd',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            borderRadius: 28,
                            color: '#333'
                          }}
                        >
                          <span>{item.title}</span>
                          <ChevronDown
                            size={20}
                            style={{
                              transform: openIndex === index ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s ease'
                            }}
                          />
                        </button>

                        <div
                          style={{
                            maxHeight: openIndex === index ? '500px' : '0px',
                            overflow: 'hidden',
                            transition: 'max-height 0.3s ease',
                            borderTop: 'none',
                            padding: openIndex === index ? '10px' : '0 10px',
                            backgroundColor: '#fff'
                          }}
                        >
                          {item.content}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <Reassurance />
              
              <div className='desktop-block-2'>
              <div>
                <p style={{fontSize: 40, margin: '40px 20px 0px 20px', color: "#333", fontWeight: 700}}>
                  Ajustement ergonomique
                </p>
                <div style={{ borderRadius: 28, padding: '20px', margin: '20px', backgroundColor: '#fff' }}>
                  <p>
                    Grâce à un système de réglage à molette ergonomique,
                    le casque enveloppe la tête pour un maintien impeccable.
                    Cette technologie assure une stabilité parfaite
                    même en cas de secousse ou de choc.
                  </p>
                  <ul>
                    <li>Tailles : S/M (55 cm – 58 cm) ou L/XL (59 cm – 63 cm)</li>
                    <li>Réglage rapide et sans effort</li>
                    <li>Confort et maintien optimaux</li>
                  </ul>
                  <img src="/assets/products/H01_molette.png" style={{maxWidth: '100%'}} alt="Casque intelligent"/>
                </div>
              </div>
              
              <div style={{margin: '40px 20px', borderRadius: 28, overflow: 'hidden'}}>
                <img src="/assets/products/h01_explanation.png" style={{maxWidth: '100%'}}  alt="Casque intelligent"/>
              </div>
            </div>

              {/* Bloc sur la télécommande */}
            <div className='desktop-block-2' style={{ alignItems: 'stretch'}}>
              <div>
                <p style={{fontSize: 40, margin: '40px 20px 0px 20px', color: "#333", fontWeight: 700}} id="size">
                  Clignotants
                </p>
                <p style={{minHeight: '40px', width: '80%', marginLeft: 20}}>Les clignotants émettent un petit son pour vous indiquer qu’ils sont bien activés.</p>
                <div style={{margin: '40px 20px', overflow: 'hidden'}}>
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="productpage-video"
                >
                  <source src="/assets/videos/cligno.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                </div>
              </div>

              <div>
                <p style={{fontSize: 40, margin: '40px 20px 0px 20px', color: "#333", fontWeight: 700}} id="size">
                  Feu stop
                </p>
                <p style={{minHeight: '40px', width: '80%', marginLeft: 20}}>Le feu stop s’active automatiquement dès que vous freinez,<br/> pour signaler clairement votre ralentissement.</p>
                <div style={{margin: '40px 20px', overflow: 'hidden'}}>
                  <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="productpage-video"
                  >
                    <source src="/assets/videos/stop.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            </div>

            <div className='desktop-block-2'>
              <div>
                <div style={{margin: '40px 20px', overflow: 'hidden'}}>
                  <img src="/assets/other/AF_H01.png" style={{maxWidth: '100%', borderRadius: 28}}  alt="Casque intelligent"/>
                </div>
              </div>

              <div>
                <p style={{fontSize: 40, margin: '40px 20px 0px 20px', color: "#333", fontWeight: 700}} id="size">
                  Feu avant
                </p>
                <p style={{margin: '40px 20px 0px 20px', color: "#333"}}>
                Le feu avant du H01 ne sert pas seulement à éclairer, il est aussi là pour vous rendre visible en toutes circonstances.
                Quand vous activez un clignotant, le feu avant se met automatiquement à clignoter lui aussi : un signal clair qui capte l’attention et renforce votre présence sur la route.
                Un petit détail qui fait une grande différence pour inciter les autres usagers à redoubler de vigilance.
                </p>
              </div>
            </div>
            
            <div className='desktop-block-2'>
              <div>
                <p style={{fontSize: 40, margin: '40px 20px 0px 20px', color: "#333", fontWeight: 700}}>
                  Télécommande
                </p>
                <div style={{ borderRadius: 28, padding: '20px', margin: '20px', backgroundColor: '#fff' }}>
                  <p>
                    Pour une sécurité accrue, notre casque intelligent peut être commandé grâce à une télécommande pratique, conçue pour être fixée sur le guidon. 
                    Elle vous permet d’activer les clignotants et de contrôler l’éclairage sans jamais lâcher les mains de votre guidon, réduisant ainsi les risques d'accident.
                  </p>
                  <ul>
                    <li>
                      <strong>Installation rapide :</strong> la télécommande se fixe en quelques secondes 
                      sur tous les guidons.
                    </li>
                    <li>
                      <strong>Contrôle intuitif :</strong> grâce à des touches ergonomiques, vous activez 
                      vos clignotants en un seul geste.
                    </li>
                    <li>
                      <strong>Connexion stable :</strong> la télécommande communique sans fil avec le casque 
                      via un protocole basse consommation, assurant une excellente autonomie.
                    </li>
                    <li>
                      <strong>Sécurité renforcée :</strong> ne plus lâcher le guidon pour signaler vos changements de 
                      direction améliore grandement votre stabilité et votre visibilité.
                    </li>
                  </ul>
                  <div style={{margin: '40px 20px', overflow: 'hidden'}}>
                    <img src="/assets/other/telecommande.png" style={{maxWidth: '80%', margin: "0 auto"}}  alt="Casque intelligent"/>
                  </div>
                </div>
              </div>

              <div>
                <p style={{fontSize: 40, margin: '40px 20px 0px 20px', color: "#333", fontWeight: 700}} id="size">
                  Guide des tailles
                </p>
                <div style={{margin: '40px 20px', overflow: 'hidden'}}>
                  <img src="/assets/products/size.png" style={{maxWidth: '100%'}}  alt="Casque intelligent"/>
                </div>
              </div>
            </div>


              <br />
            </div>
          ) : (
            <div className="loader-container">
              <Loader />
            </div>
          )
        }

        {H01.data && <FixedActionBar product={selectedProduct} />}
      </div>
    </div>
  );
}

export default H01ProductPage;
